import React, { useCallback, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Alert, Box, Snackbar } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useCancelFormDeliverable } from '../../components/form-list/useCancelFormDeliverable';
import { useDeleteApiTarsByIdMutation } from '../../app/athenaApiGen';
import { FormList } from '../../components/form-list';
import DeleteConfirmation from '../../components/Modals/DeleteConfirmation';
import { WORKFLOW_PROGRESS_FILTERS } from '../../utils/workflow';
import { createIMCSTARColumns } from './IMCSTARUtils';
import { useIMCSTARList } from './useIMCSTARList';
import {
  openDeleteDialog,
  closeDeleteDialog,
  showSnackbar,
  hideSnackbar,
  setWorkflowFilter,
} from '../../app/tar-imcs/tarImcsSlice';

export default function IMCSTARList() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { isDeleteDialogOpen, tarToDelete, snackbar } = useSelector((state) => state.tarImcs);
  const workflowFilter = useSelector((state) => state.tarImcs.workflowFilter);

  const {
    paginationModel,
    setPaginationModel,
    search,
    setSearch,
    rows,
    rowCount,
    loading,
    isError,
    refetch,
    quickFilterCounts,
  } = useIMCSTARList();
  const [deleteTar, { isLoading: deleting }] = useDeleteApiTarsByIdMutation();

  const columns = useMemo(() => createIMCSTARColumns(), []);

  const handleDeleteClick = useCallback((row) => {
    dispatch(openDeleteDialog({ id: row.id, tarNumber: row.tarNumber }));
  }, [dispatch]);

  const handleDeleteConfirm = useCallback(
    async () => {
      if (!tarToDelete) return;
      try {
        await deleteTar({ id: tarToDelete.id }).unwrap();
        dispatch(closeDeleteDialog());
        dispatch(showSnackbar({ message: 'TAR deleted successfully', severity: 'success' }));
        refetch();
      } catch (error) {
        console.error('Failed to delete TAR', error);
        dispatch(closeDeleteDialog());
        dispatch(showSnackbar({ message: 'Failed to delete TAR. Please try again.', severity: 'error' }));
      }
    },
    [tarToDelete, deleteTar, refetch, dispatch],
  );

  const notifyCancel = useCallback(
    (message, severity) => dispatch(showSnackbar({ message, severity })),
    [dispatch],
  );
  const { handleCancelClick } = useCancelFormDeliverable({
    entityLabel: 'TAR',
    notify: notifyCancel,
    refetch,
  });

  const rowActions = useMemo(
    () => [
      {
        label: 'View',
        onClick: (row) => navigate(`/tar-imcs/${row.id}?mode=view`),
      },
      {
        label: 'Edit',
        onClick: (row) => navigate(`/tar-imcs/${row.id}?mode=edit`),
        hidden: (row) => row.workflowState && row.workflowState !== 'Pending',
      },
      {
        label: 'Cancel',
        onClick: (row) => handleCancelClick(row),
        hidden: (row) =>
          !row.workflowState ||
          row.workflowState === 'Pending' ||
          row.workflowState !== 'In Internal Review',
      },
      {
        label: 'Delete',
        onClick: (row) => handleDeleteClick(row),
        hidden: (row) => row.workflowState && row.workflowState !== 'Pending',
      },
    ],
    [handleDeleteClick, handleCancelClick, navigate],
  );

  return (
    <Box sx={{ p: 1, pt: 2, height: '100%', bgcolor: 'transparent' }}>
      <FormList
        title="Travel Authorization Requests (TARs)"
        columns={columns}
        rows={rows}
        rowCount={rowCount}
        loading={loading}
        error={isError}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        rowActions={rowActions}
        onRowClick={(row) => navigate(`/tar-imcs/${row.id}?mode=view`)}
        workflowFilters={WORKFLOW_PROGRESS_FILTERS}
        workflowFilter={workflowFilter}
        onWorkflowFilterChange={(value) => dispatch(setWorkflowFilter(value))}
        quickFilterCounts={quickFilterCounts}
        search={search}
        onSearchChange={setSearch}
        onRefresh={refetch}
        createButtonLabel="New TAR"
        onCreateClick={() => navigate('/tar-imcs')}
      />

      <DeleteConfirmation
        open={isDeleteDialogOpen}
        title="Delete TAR"
        onClose={() => dispatch(closeDeleteDialog())}
        content={{
          mainText: `
            Are you sure you want to delete TAR <strong>${tarToDelete?.tarNumber}</strong>?
            <br /><br />
            This action cannot be undone.`,
        }}
        handleConfirmation={handleDeleteConfirm}
        isLoading={deleting}
      />

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => dispatch(hideSnackbar())}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => dispatch(hideSnackbar())}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
